/**
 * Tiny DOM helpers. Shared by every page.
 *
 * @module web/js/dom
 */

import { APP_NAME, APP_TAGLINE } from './brand.ts';

/** Attributes accepted by `el`. `class` sets className, `text` sets textContent. */
export interface ElProps {
  class?: string;
  text?: string;
  html?: string;
  title?: string;
  href?: string;
  src?: string;
  alt?: string;
  type?: string;
  id?: string;
  role?: string;
  style?: string;
  dataset?: Record<string, string>;
  attrs?: Record<string, string>;
  on?: Partial<Record<keyof HTMLElementEventMap, (e: Event) => void>>;
}

/** Create an element with attributes, text, and children in one call. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElProps = {},
  children: Array<Node | string | null | undefined> = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props.class) node.className = props.class;
  if (props.text !== undefined) node.textContent = props.text;
  if (props.html !== undefined) node.innerHTML = props.html;
  if (props.title) node.title = props.title;
  if (props.id) node.id = props.id;
  if (props.role) node.setAttribute('role', props.role);
  if (props.style) node.setAttribute('style', props.style);
  if (props.href) (node as HTMLAnchorElement).href = props.href;
  if (props.src) (node as HTMLImageElement).src = props.src;
  if (props.alt !== undefined) (node as HTMLImageElement).alt = props.alt;
  if (props.type) (node as HTMLInputElement).type = props.type;
  if (props.dataset) for (const [k, v] of Object.entries(props.dataset)) node.dataset[k] = v;
  if (props.attrs) for (const [k, v] of Object.entries(props.attrs)) node.setAttribute(k, v);
  if (props.on) {
    for (const [k, fn] of Object.entries(props.on)) {
      node.addEventListener(k, fn as EventListener);
    }
  }
  for (const c of children) {
    if (c === null || c === undefined) continue;
    node.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

/** Format a number for display, short and finite-safe. */
export function fmt(v: number | undefined, digits = 2): string {
  if (v === undefined || !Number.isFinite(v)) return '—';
  return v.toFixed(digits);
}

/** Format a fraction as a percentage. */
export function pct(v: number | undefined, digits = 2): string {
  if (v === undefined || !Number.isFinite(v)) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

/** A `<dt>`/`<dd>` pair for a `<dl class="metrics">`. */
export function metric(label: string, value: string): HTMLDivElement {
  return el('div', {}, [el('dt', { text: label }), el('dd', { text: value })]);
}

/** The provenance tag. Says "approximated" until the matrices are verified. */
export function provenanceTag(provenance: string): HTMLElement {
  const approximated = provenance !== 'verified';
  return el('span', {
    class: approximated ? 'tag tag--approx' : 'tag tag--ok',
    text: provenance,
    title: approximated
      ? 'Our own first-principles matrix, not DStretch’s.'
      : 'Differential-tested against the real DStretch.',
  });
}

/** Replace `parent`'s children with `nodes`. */
export function replaceChildren(parent: HTMLElement, nodes: Array<Node | string>): void {
  parent.replaceChildren(...nodes);
}

/** Fetch JSON, returning `null` for any error. Used where absence is normal. */
export async function fetchJsonOrNull<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { cache: 'no-cache' });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

/** A page of the app, as the navigation knows it. */
export type PageKey = 'camera' | 'gallery' | 'upload';

/**
 * The navigation: page key, href, label.
 *
 * The hrefs have no leading slash on purpose. Locally the app is served from
 * `/`, but a GitHub Pages **project** site serves it from `/dstretch/`, and a
 * root-absolute `/gallery.html` would leave the app entirely. Relative hrefs are
 * correct in both places, because every page sits at the same level.
 */
const NAV: Array<[PageKey, string, string]> = [
  ['camera', './', 'Camera'],
  ['gallery', 'gallery.html', 'Gallery'],
  ['upload', 'upload.html', 'Upload'],
];

/** The page header, shared by every page. */
export function header(current: PageKey): HTMLElement {
  const nav = el('nav', { class: 'nav' }, NAV.map(([key, href, label]) =>
    el('a', { href, text: label, attrs: key === current ? { 'aria-current': 'page' } : {} }),
  ));
  return el('header', { class: 'top' }, [
    el('div', { class: 'wrap' }, [
      el('div', { class: 'top__row' }, [
        el('h1', { text: APP_NAME }),
        el('p', { class: 'top__sub', text: APP_TAGLINE }),
      ]),
      nav,
    ]),
  ]);
}
